import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import i18next from 'i18next';
import styled from 'styled-components';
import { Button } from '@famly/mf_input_button';
import { Box } from '@famly/mf_layout_box';
import { createStack } from '@famly/mf_layout_stack';
// Touch screens swipe the row; a mouse gets a button over each edge with more times beyond it, on a fade so a time
// cut off under it reads as continuing. Only the button takes clicks, so the times under the fade stay tappable
const EdgeButton = styled(createStack({ position: 'absolute', top: 0, bottom: 0, width: '72px', alignItems: 'center', pointerEvents: 'none' })) `
    ${({ $side }) => ($side === 'start' ? 'left: 0; justify-content: flex-start;' : 'right: 0; justify-content: flex-end;')}
    background: linear-gradient(
        ${({ $side }) => ($side === 'start' ? 'to right' : 'to left')},
        ${props => props.theme.mf.colorPalette.n25} 40%,
        transparent
    );

    & > * {
        pointer-events: auto;
    }

    @media (hover: none), (pointer: coarse) {
        && {
            display: none;
        }
    }
`;
// The row scrolls by swipe, trackpad or the buttons; a scrollbar under it would only add a line of chrome
const ScrollingRow = styled(createStack({ flexWrap: 'nowrap', overflowX: 'auto', gap: 3, paddingY: 1 })) `
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;
/**
 * Times on one line that scrolls sideways, so the questions under them stay put. It opens on the latest times, the
 * ones most often chosen for a pick-up or the end of a shift
 */
export const KioskTimeRow = ({ children, initialIndex }) => {
    const rowRef = React.useRef(null);
    const [canScroll, setCanScroll] = React.useState({ back: false, forward: false });
    const updateCanScroll = () => {
        const row = rowRef.current;
        if (!row) {
            return;
        }
        setCanScroll({
            back: row.scrollLeft > 1,
            forward: row.scrollLeft + row.clientWidth < row.scrollWidth - 1,
        });
    };
    // Only where the row opens; later choices leave the scroll where the user put it
    const onRowOpened = React.useEffectEvent((row) => {
        const target = initialIndex === null ? row.lastElementChild : row.children[initialIndex];
        if (target instanceof HTMLElement) {
            row.scrollLeft += Math.max(0, target.getBoundingClientRect().right - row.getBoundingClientRect().right);
        }
        updateCanScroll();
    });
    const onRowResized = React.useEffectEvent(updateCanScroll);
    // Before the first paint, so the row never shows the early times and then jumps
    React.useLayoutEffect(() => {
        if (rowRef.current) {
            onRowOpened(rowRef.current);
        }
    }, []);
    React.useEffect(() => {
        const row = rowRef.current;
        if (!row || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => onRowResized());
        observer.observe(row);
        return () => observer.disconnect();
    }, []);
    const scrollBy = (direction) => { var _a; return (_a = rowRef.current) === null || _a === void 0 ? void 0 : _a.scrollBy({ left: direction * rowRef.current.clientWidth * 0.8, behavior: 'smooth' }); };
    return (_jsxs(Box, { position: "relative", children: [_jsx(ScrollingRow, { ref: rowRef, onScroll: updateCanScroll, children: children }), canScroll.back ? (_jsx(EdgeButton, { "$side": "start", children: _jsx(Button, { icon: "chevron_left", variant: "secondary", size: "compact", onClick: () => scrollBy(-1), "aria-label": i18next.t($ => $.stat.signinSelectChildren.lib.src.translations.json.times.earlier), "data-e2e-id": "kiosk-time-row-back" }) })) : null, canScroll.forward ? (_jsx(EdgeButton, { "$side": "end", children: _jsx(Button, { icon: "chevron_right", variant: "secondary", size: "compact", onClick: () => scrollBy(1), "aria-label": i18next.t($ => $.stat.signinSelectChildren.lib.src.translations.json.times.later), "data-e2e-id": "kiosk-time-row-forward" }) })) : null] }));
};
